import { useMemo } from 'react';
import {
  useUtopiesDoubleMaterialityArrays_ReferentialQuery,
  UtopiesDoubleMaterialityArrays_AnswerFragment,
  UtopiesDoubleMaterialityArrays_QuestionFragment,
} from '@/graphql/generated';
import { MessageBox, MessageBoxType } from '@/components/layout/MessageBox';
import { useProjectContext } from '@/providers/ProjectContextProvider';
import { StakesDoubleMaterialitySeverityArray } from '../materiality/StakesDoubleMaterialitySeverityArray';
import { StakesDoubleMaterialityProbabilityArray } from '../materiality/StakesDoubleMaterialityProbabilityArray';
import { StakesDoubleMaterialityComments } from '../materiality/StakesDoubleMaterialityComments';
import { StakesDoubleMaterialityQualityArray } from '../materiality/StakesDoubleMaterialityQualityArray';
import { useTranslation } from '@/hooks/useTranslation';

export function UtopiesDoubleMaterialityArrays({
  question,
  answers,
  isThumbnail,
}: {
  question: UtopiesDoubleMaterialityArrays_QuestionFragment;
  answers: UtopiesDoubleMaterialityArrays_AnswerFragment[];
  isThumbnail?: boolean;
}) {
  const { t, translateProperty } = useTranslation();
  // Fetch stakes
  const projectContext = useProjectContext();
  const companyReferentialDocumentQuery =
    useUtopiesDoubleMaterialityArrays_ReferentialQuery({
      variables: { id: projectContext?.enterprise?.referential?.id || '' },
      skip: !projectContext?.enterprise?.id,
      fetchPolicy: 'cache-and-network',
    });
  const referential = companyReferentialDocumentQuery.data?.referential;
  const stakes = useMemo(() => {
    if (question.pillar?.id) {
      return (
        referential?.pillars
          .filter((pillar) => pillar.id === question.pillar?.id)
          .flatMap((pillar) => pillar.stakes)
          .toSorted((a, b) =>
            translateProperty(a, 'name').localeCompare(
              translateProperty(b, 'name'),
            ),
          ) || []
      );
    } else {
      return (
        referential?.pillars
          .flatMap((pillar) => pillar.stakes)
          .toSorted((a, b) =>
            translateProperty(a, 'name').localeCompare(
              translateProperty(b, 'name'),
            ),
          ) || []
      );
    }
  }, [question.pillar?.id, referential?.pillars, translateProperty]);

  // TODO: fix preview when in isThumbnail mode (theme modal)
  if (isThumbnail) {
    return (
      <div className="p-4">
        <MessageBox type={MessageBoxType.Info}>
          {t('global:featureNotImplemented')}
        </MessageBox>
      </div>
    );
  }

  if (answers.length === 0) {
    return (
      <MessageBox type={MessageBoxType.Error}>
        {t('survey.response.no_answer')}
      </MessageBox>
    );
  }

  return (
    <div className="space-y-8">
      <StakesDoubleMaterialityQualityArray
        stakes={stakes}
        question={question}
        answers={answers}
      />
      <StakesDoubleMaterialitySeverityArray
        stakes={stakes}
        question={question}
        answers={answers}
      />
      <StakesDoubleMaterialityProbabilityArray
        stakes={stakes}
        question={question}
        answers={answers}
      />
      <StakesDoubleMaterialityComments stakes={stakes} answers={answers} />
    </div>
  );
}
